import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getHomePath } from '../context/AuthContext';
import { localLogin } from '../api';
import AuthShell, { AuthField, CodeInput } from '../components/AuthShell';

// The sign-in page keeps its original look, form included. Local (email, password and code) sign-in itself is switched off on
// the server (LOCAL_LOGIN_ENABLED in app.js), which refuses every attempt with the same plain "sign-in failed" as a wrong password.
const LOCAL_LOGIN_ENABLED = true;

export default function LoginPage() {
  const { loginWithMicrosoft, completeLocalLogin, authError } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isLocalSigningIn, setIsLocalSigningIn] = useState(false);

  async function handleMicrosoftLogin() {
    setError('');
    setIsSigningIn(true);
    try {
      // Navigates the whole page away to Microsoft — on success this
      // never resolves here, the browser just leaves. AuthContext picks
      // the result back up after Microsoft redirects back.
      await loginWithMicrosoft();
    } catch (err) {
      console.error('Login error:', err);
      setError('Login mislykkedes. Prøv igen.');
      setIsSigningIn(false);
    }
  }

  async function handleLocalLogin(e) {
    e.preventDefault();
    setError('');
    if (totpCode.length !== 6) {
      setError('Indtast den 6-cifrede kode fra din godkender-app.');
      return;
    }
    setIsLocalSigningIn(true);
    try {
      const result = await localLogin(email.trim(), password, totpCode.trim());
      if (!result.success) {
        setError(result.error || 'Login mislykkedes. Prøv igen.');
        setTotpCode('');
        return;
      }
      completeLocalLogin(result);
      navigate(getHomePath(result.user.role, result.user.tenantIds));
    } catch (err) {
      console.error('Local login error:', err);
      setError('Login mislykkedes. Prøv igen.');
    } finally {
      setIsLocalSigningIn(false);
    }
  }

  const expired = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('expired') === '1';
  const locked = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('locked') === '1';
  const shownError = error || authError || (locked ? 'Din konto er låst. Kontakt din administrator.' : expired ? 'Din session er udløbet. Log ind igen.' : '');

  return (
    <AuthShell>
      <h1>Velkommen tilbage</h1>
      <p className="au-sub">Log ind for at fortsætte til jeres overblik.</p>

      {LOCAL_LOGIN_ENABLED && (
      <>
      <form onSubmit={handleLocalLogin}>
        <AuthField label="Email" icon="mail">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLocalSigningIn}
            autoComplete="username"
            required
            autoFocus
          />
        </AuthField>
        <AuthField label="Adgangskode" icon="lock">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLocalSigningIn}
            autoComplete="current-password"
            required
          />
        </AuthField>
        <div className="au-field">
          <span>Kode fra godkender-appen</span>
          <CodeInput value={totpCode} onChange={setTotpCode} disabled={isLocalSigningIn} bad={!!error && totpCode.length === 0} />
        </div>
        {shownError && <p className="au-msg" role="alert">{shownError}</p>}
        <button type="submit" className="au-btn" disabled={isLocalSigningIn}>
          {isLocalSigningIn ? 'Logger ind…' : 'Log ind'}
        </button>
      </form>

      <div className="au-or">eller</div>
      </>
      )}
      {!LOCAL_LOGIN_ENABLED && shownError && <p className="au-msg" role="alert">{shownError}</p>}

      <button type="button" className="au-ms" onClick={handleMicrosoftLogin} disabled={isSigningIn}>
        <svg width="17" height="17" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
        {isSigningIn ? 'Logger ind…' : 'Log ind med Microsoft'}
      </button>

      <p className="au-foot">Har du brug for hjælp? <b>support@nordcomply.dk</b></p>
    </AuthShell>
  );
}
